import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateCampaignContentSettings } from '../../../queries';
import { readImageFit } from '../../../utils/imageFit';
import MediaThumb from './components/MediaThumb';
import { Button } from '../../../components/ui/Button';

const FITS = ['fit', 'crop', 'fill'];

/**
 * Builder-only item settings sheet — .claude/pp-campaign-builder-prototype.html's
 * openItem(). Opened from a row's gear button in CampaignCard.jsx (MAIN) and
 * CampaignVibesSection.jsx (vibes playlist rows). Does NOT reuse/modify
 * MediaSettingPopup.jsx / PlaylistSettingPopup.jsx — those stay pixel-identical
 * for their other callers (the standalone Playlist page) — but saves through
 * the exact SAME hook/endpoint both of those already use in the campaign
 * builder: `useUpdateCampaignContentSettings` → POST /campaign/media/{id}/setting,
 * keyed by campaign_playlist_media_id (true for BOTH a plain media row and a
 * playlist-reference row — confirmed PlaylistSettingPopup.jsx never actually
 * calls the playlist-level endpoint despite its name).
 *
 * ⚠️ Layer-tracking fix (F3): `layer` is a REQUIRED explicit prop, never read
 * from shared redux `layer` state — the old MediaSettingPopup/PlaylistSettingPopup
 * read redux `layer`, which the vibes section's own edit flow leaves on
 * PLAYGROUND, so a MAIN row's gear could silently inherit the wrong layer.
 *
 * No downloadable toggle anywhere (F4) — intentionally omitted.
 */
function BuilderItemSettingsSheet({ item, layer, isPlaylist = false, unifyActive = false, onClose }) {
  const { t } = useTranslation();
  const updateCampaignItemSetting = useUpdateCampaignContentSettings();

  const [show, setShow] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const [setting, setSetting] = useState({
    is_muted: layer === 'PLAYGROUND' ? true : !!item.is_muted,
    image_fit: readImageFit(item) || 'crop',
    show_layer_one: item.show_layer_one !== undefined ? !!item.show_layer_one : false,
    show_layer_two: !!item.show_layer_two,
    show_layer_three: !!item.show_layer_three,
  });

  const close = () => {
    setShow(false);
    setTimeout(() => onClose?.(), 260);
  };

  const save = async () => {
    const formData = new FormData();
    formData.append('is_muted', Number(setting.is_muted));
    formData.append('image_fit', setting.image_fit);
    if (layer === 'MAIN') {
      formData.append('show_layer_one', Number(setting.show_layer_one));
      formData.append('show_layer_two', Number(setting.show_layer_two));
      formData.append('show_layer_three', Number(setting.show_layer_three));
    }
    await updateCampaignItemSetting.mutateAsync({
      data: formData,
      id: item.campaign_playlist_media_id,
      suppressDialog: true,
    });
    close();
  };

  const title = isPlaylist ? t('show.show_item_sheet_title_playlist') : t('show.show_item_sheet_title_media');
  const sub = isPlaylist
    ? t('show.show_item_sheet_sub_playlist')
    : t('show.show_item_sheet_sub_media');
  const itemName = item.file_name || item.playlist_title || item.title || '';

  return (
    <>
      <div className={`pp-cb-scrim ${show ? 'show' : ''}`} onClick={close}></div>
      <div className={`pp-cb-sheet ${show ? 'show' : ''}`} role="dialog" aria-label={title}>
        <div className="pp-cb-grabber"></div>
        <h3>{title}</h3>
        <div className="pp-cb-sheet-sub">{sub}</div>

        <div className="pp-cb-sheet-body">
          <div className="pp-cb-ihead">
            <div className="pp-cb-item-thumb"><MediaThumb item={item} /></div>
            <div className="n">{itemName}</div>
          </div>

          {unifyActive && (
            <div className="pp-cb-warn uni">
              <div className="wt">
                <i className="pi pi-exclamation-triangle" />
                <span>{t('show.show_item_unify_notice')}</span>
              </div>
            </div>
          )}

          <div className="pp-cb-stitle">{t('show.show_media_settings_title')}</div>
          <div className="pp-cb-sgroup">
            <div className="pp-cb-srow">
              <div className="sl">
                <div className="a">{t('dragdrop.mute')}</div>
                {layer === 'PLAYGROUND' && <div className="b">{t('show.show_vibes_always_muted_note')}</div>}
              </div>
              <button
                type="button"
                className={`pp-cb-sw ${setting.is_muted ? 'on' : ''}`}
                role="switch"
                aria-checked={setting.is_muted}
                disabled={layer === 'PLAYGROUND'}
                onClick={() => setSetting((p) => ({ ...p, is_muted: !p.is_muted }))}
              />
            </div>

            <div className="pp-cb-fitbox">
              <div className="a">{t('dragdrop.image_fit.label')}</div>
              <div className="pp-cb-seg wide">
                {FITS.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={setting.image_fit === mode ? 'on' : ''}
                    onClick={() => setSetting((p) => ({ ...p, image_fit: mode }))}
                  >
                    {t(`dragdrop.image_fit.${mode}`)}
                  </button>
                ))}
              </div>
              <div className="pp-cb-fit-hint">{t(`show.show_fit_hint_${setting.image_fit}`)}</div>
            </div>
          </div>

          {layer === 'MAIN' && (
            <>
              <div className="pp-cb-stitle">{t('show.show_item_overlays_title')}</div>
              <div className="pp-cb-sgroup">
                <div className="pp-cb-srow">
                  <div className="sl">
                    <div className="a">{t('show.show_show_playground_layer')}</div>
                    <div className="b">{t('show.show_item_show_layer_one_hint')}</div>
                  </div>
                  <button
                    type="button"
                    className={`pp-cb-sw ${setting.show_layer_one ? 'on' : ''}`}
                    role="switch"
                    aria-checked={setting.show_layer_one}
                    onClick={() => setSetting((p) => ({ ...p, show_layer_one: !p.show_layer_one }))}
                  />
                </div>
                <div className="pp-cb-srow">
                  <div className="sl"><div className="a">{t('show.show_show_corner_layer')}</div></div>
                  <button
                    type="button"
                    className={`pp-cb-sw ${setting.show_layer_two ? 'on' : ''}`}
                    role="switch"
                    aria-checked={setting.show_layer_two}
                    onClick={() => setSetting((p) => ({ ...p, show_layer_two: !p.show_layer_two }))}
                  />
                </div>
                <div className="pp-cb-srow">
                  <div className="sl"><div className="a">{t('show.show_show_banner_layer')}</div></div>
                  <button
                    type="button"
                    className={`pp-cb-sw ${setting.show_layer_three ? 'on' : ''}`}
                    role="switch"
                    aria-checked={setting.show_layer_three}
                    onClick={() => setSetting((p) => ({ ...p, show_layer_three: !p.show_layer_three }))}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <Button label={t('buttons.save')} className="w-100" onClick={save} loading={updateCampaignItemSetting.isLoading} />
          <Button label={t('buttons.cancel')} outlined className="w-100" style={{ marginTop: 10 }} onClick={close} />
        </div>
      </div>
    </>
  );
}

export default BuilderItemSettingsSheet;
